import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { MODAL_CONTENT_CLASS } from '../constants/styles';
import { normalizeAlert } from '../alerts/normalize-alert';
import { formatTimestamp } from '../i18n/formatters';

function formatDate(value) {
  try {
    return formatTimestamp(value);
  } catch (err) {
    return '—';
  }
}

function AlertListModal({ alerts = [], devices = [], onClose, isLoading, error }) {
  const { t } = useTranslation(['devices', 'ui']);

  const normalizedAlerts = useMemo(() => {
    if (!Array.isArray(alerts)) return [];
    return alerts
      .map((alert) => normalizeAlert(alert))
      .filter(Boolean);
  }, [alerts]);
  const hasAlerts = normalizedAlerts.length > 0;

  const deviceNameBySerial = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(devices)) return map;
    devices.forEach((device) => {
      if (!device?.serialNumber) return;
      map.set(device.serialNumber, device.readerName || device.name || device.serialNumber);
    });
    return map;
  }, [devices]);

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 flex items-center justify-center">
        <DialogPanel className={MODAL_CONTENT_CLASS}>
          <DialogTitle className="text-lg font-medium text-gray-900 mb-4">{t('devices:alerts.modalTitle')}</DialogTitle>
          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
          {isLoading ? (
            <div className="flex justify-center items-center py-4" role="status" aria-label={t('ui:loading')}>
              <svg
                className="animate-spin h-5 w-5 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          ) : hasAlerts ? (
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm" role="table">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                      {t('devices:alerts.columns.deviceName')}
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                      {t('devices:alerts.columns.deviceSerial')}
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                      {t('devices:alerts.columns.type')}
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                      {t('devices:alerts.columns.message')}
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                      {t('devices:alerts.columns.createdAt')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {normalizedAlerts.map((alert, idx) => {
                    const deviceName = deviceNameBySerial.get(alert.deviceSerial) || '—';
                    const typeSlug = alert.type?.replace(/\./g, '_');
                    const typeKey = `devices:alerts.types.${typeSlug}`;
                    const messageKey = `devices:alerts.messages.${typeSlug}`;
                    const message = alert.type === 'device.offline'
                      ? t(messageKey, { device: deviceName, defaultValue: alert.message })
                      : (alert.message || alert.description || '—');
                    return (
                      <tr key={alert.id || alert._id || `${alert.deviceSerial}-${idx}`}>
                        <td className="px-4 py-2 text-gray-900">{deviceName}</td>
                        <td className="px-4 py-2 text-gray-900">{alert.deviceSerial || '—'}</td>
                        <td className="px-4 py-2 text-gray-700">{t(typeKey, { defaultValue: alert.type || '—' })}</td>
                        <td className="px-4 py-2 text-gray-700">{message}</td>
                        <td className="px-4 py-2 text-gray-700">{formatDate(alert.createdAt || alert.timestamp || alert.time)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('devices:alerts.empty')}</p>
          )}
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="btn-secondary" aria-label={t('ui:modal.close')}>
              {t('ui:modal.close')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default AlertListModal;
